import { c as _c } from "react/compiler-runtime";
import { IcoChevronLeft16, IcoClose16 } from '@onefootprint/icons';
import { Badge, IconButton, LoadingSpinner } from '@onefootprint/ui';
import { ReactFlowProvider } from '@xyflow/react';
import { useTranslation } from 'react-i18next';
import PlaybookBuilder, { CreateDialog, useConfirmClose, useStore } from '@/components/playbook-builder';
import SandboxToggle from '@/components/sandbox-toggle';
import PlaybookGraphHeader from '@/features/playbook-graph-header';
import ActionsDropdown from '../actions-dropdown';
import DraftTestMode from './draft-test-mode';
type PlaybookDraftEditProps = {
  /** Whether the top-left exit control goes back to the template picker or out to the playbooks list. */
  exit: 'back-to-templates' | 'close';
  onExit: () => void;
  onCreated: (playbookId: string) => void;
};

/**
 * Full editor for an uncommitted draft (a `/playbooks/new` template or a deep-linked Percy draft). It runs
 * off the store's edit session — a draft has no committed version — so it drops the version/publish chrome
 * and names-and-creates via `CreateDialog`; everything else matches the committed editor.
 */
const PlaybookDraftEdit = t0 => {
  const $ = _c(29);
  const {
    exit,
    onExit,
    onCreated
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "draftEditor"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const store = useStore();
  let t2;
  if ($[1] !== onExit || $[2] !== store.data.history.hasChanges) {
    t2 = {
      isDirty: store.data.history.hasChanges,
      isMutationPending: false,
      onCloseParent: onExit
    };
    $[1] = onExit;
    $[2] = store.data.history.hasChanges;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    attemptClose
  } = useConfirmClose(t2);
  if (!store.data.sessionId) {
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <LoadingSpinner />;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  let t3;
  if ($[5] !== attemptClose.handler || $[6] !== exit || $[7] !== t) {
    t3 = exit === "back-to-templates" ? <button type="button" onClick={attemptClose.handler} className="inline-flex gap-1 text-label-2 text-primary items-center cursor-pointer shrink-0 max-md:size-8 max-md:justify-center max-md:rounded-(--icon-button-border-radius) max-md:hover:bg-hover-control"><IcoChevronLeft16 color="primary" /><span className="max-md:sr-only">{t(_temp)}</span></button> : <IconButton aria-label={t(_temp2)} onClick={attemptClose.handler} size="compact" icon={IcoClose16} />;
    $[5] = attemptClose.handler;
    $[6] = exit;
    $[7] = t;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const exitControl = t3;
  let t4;
  if ($[9] !== store.data.settings.name || $[10] !== t) {
    t4 = store.data.settings.name || t(_temp3);
    $[9] = store.data.settings.name;
    $[10] = t;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t4) {
    t5 = <ActionsDropdown isDraft={true} name={t4} />;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t) {
    t6 = t(_temp4);
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t6) {
    t7 = <Badge variant="neutral">{t6}</Badge>;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== exitControl || $[19] !== t5 || $[20] !== t7) {
    t8 = <div className="flex gap-2 items-center min-w-0">{exitControl}{t5}{t7}</div>;
    $[18] = exitControl;
    $[19] = t5;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <DraftTestMode />;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== onCreated) {
    t10 = <>{t9}<div className="max-md:order-last max-md:ml-auto"><CreateDialog onDone={onCreated} /></div></>;
    $[23] = onCreated;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <SandboxToggle />;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t10 || $[27] !== t8) {
    t12 = <div className="flex flex-col h-full min-h-0 overflow-hidden"><ReactFlowProvider><PlaybookBuilder className="flex-1 min-h-0" headerComponent={<PlaybookGraphHeader slots={{
          left: t8,
          right: t10,
          end: t11
        }} />} /></ReactFlowProvider></div>;
    $[26] = t10;
    $[27] = t8;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  return t12;
};
export default PlaybookDraftEdit;
function _temp($) {
  return $.backToTemplates;
}
function _temp2($_0) {
  return $_0.close;
}
function _temp3($_1) {
  return $_1.untitledDraft;
}
function _temp4($_2) {
  return $_2.draftBadge;
}